<template>
  <div
    v-if="!isIntegrationUnavailable"
    class="insights-team-collaboration-widget insights-widget"
  >
    <InsightsIntegrationFailureDialog
      v-if="showFailureDialog"
      :show="showFailureDialog"
      @close="toggleFailureDialog"
    />

    <div
      class="widget-wrapper"
      :class="{ 'blurred-bg': isStillSyncing }"
    >
      <div class="widget-common widget-small widget-full-height">
        <div class="widget-title d-flex align-items-center">
          <span>{{ title }}</span>
          <i
            v-tooltip="tooltipOptions"
            class="hi hi-info-filled ml-5"
            :title="tooltip"
          />
        </div>
        <div
          class="widget-body"
          :class="bodyClasses"
        >
          <div
            v-if="integrationExists"
            class="d-flex"
            :class="isIntegrationSyncing ? null : bodyClasses"
          >
            <div
              v-if="isReady || isIntegrationSyncing"
              class="insights-team-collaboration-widget-content"
            >
              <template v-if="isIntegrationSynced">
                <template v-if="!isEmptyWidget">
                  <div class="section">
                    <InsightsTeamCollaborationPill
                      v-for="[property, value] in pillSection('email')"
                      :key="`${property}_${value}`"
                      :property="property"
                      :value="value"
                      type="email"
                    />
                  </div>
                  <div class="section">
                    <InsightsTeamCollaborationPill
                      v-for="[property, value] in pillSection('call')"
                      :key="`${property}_${value}`"
                      :property="property"
                      :value="value"
                      type="call"
                    />
                    <InsightsTeamCollaborationPill
                      v-for="[property, value] in pillSection('message')"
                      :key="`${property}_${value}`"
                      :property="property"
                      :value="value"
                      type="message"
                    />
                  </div>
                </template>
                <InsightsNoData
                  v-else
                  :type="emptyReason"
                />
              </template>
              <InsightsIntegrationSyncStatus
                v-else
                :is-still-syncing="isStillSyncing"
              />
            </div>
          </div>
          <InsightsWidgetIntegration
            v-else-if="isReady"
            :widget-key="widgetKey"
            :widget-data="widgetData"
          >
            <template #description>
              Connect your organization's communication platform to measure how your teams are engaging in collaboration
            </template>
          </InsightsWidgetIntegration>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { INSIGHTS } from '~/app_constants';
import { mapGetters } from 'vuex';
import { Insights, Integrations } from '~/mixins';
import InsightsTeamCollaborationPill from '~/components/insights/InsightsTeamCollaborationPill.vue';
import InsightsWidgetIntegration from '~/components/insights/InsightsWidgetIntegration.vue';
import InsightsIntegrationSyncStatus from '~/components/insights/InsightsIntegrationSyncStatus.vue';
import InsightsIntegrationFailureDialog from '~/components/insights/InsightsIntegrationFailureDialog.vue';

export default {
  name: 'InsightsTeamCollaborationWidget',
  components: {
    InsightsTeamCollaborationPill,
    InsightsWidgetIntegration,
    InsightsIntegrationSyncStatus,
    InsightsIntegrationFailureDialog,
    InsightsNoData: defineAsyncComponent(() => import('~/components/insights/InsightsNoData.vue')),
  },
  mixins: [Insights, Integrations],
  data: () => ({
    widgetKey: INSIGHTS.api.collaborations,
    tooltip: INSIGHTS.tooltips.collaborations.content,
    retryFetch: INSIGHTS.collaborations.retryFetch,
    tooltipOptions: Object.freeze({
      placement: 'top',
      html: true,
    }),
    title: 'Team Collaboration',
  }),
  computed: {
    ...mapGetters({
      widgetData: 'collaborations',
    }),
    integrationStatus() {
      return this.widgetData?.collaboration_integration_status;
    },
    pills() {
      return Object.entries(this.widgetData)
        .filter(([key, value]) => typeof value === 'object')
        .map(([key, value]) => {
          let data = value;

          if (key === 'email') {
            data = {
              sent_count: value.sent_count,
              received_count: value.received_count,
              unread_count: Math.max(0, value.received_count - value.read_count),
            };
          }

          if (key === 'message') {
            data = {
              total_count: value.public_message_count + value.private_message_count,
            };
          }

          return { type: key, data: Object.entries(data) };
        });
    },
  },
  async created() {
    await this.fetch();
    this.checkFailureStatus();
  },
  methods: {
    pillSection(section) {
      const pill = this.pills?.find((pill) => pill.type === section);

      return pill?.data || [];
    },
  },
};
</script>
